// File: src/TermsAndConditions.jsx
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";

const brand = {
  primary: "#B1336E",
  accentGold: "#E6B43C",
  accentGreen: "#67A43C",
};

const container = "mx-auto w-full max-w-5xl px-4 sm:px-6 lg:px-8";
const fadeUp = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, ease: "easeOut" },
};

const scrollTo = (id) => (e) => {
  e?.preventDefault?.();
  const targetId = (id || "").replace(/^#/, "");
  const el = document.getElementById(targetId);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    // optional: reflect in URL so deep links work
    const base = window.location.hash.split("#")[1] || ""; // HashRouter base (e.g. "/terms")
    const basePath = base.includes("/") ? base.split("?")[0] : base;
    window.history.replaceState(null, "", `#${basePath}`);
  } else {
    // fallback
    window.location.hash = `#${targetId}`;
  }
};

const toc = [
  ["Alcance del servicio", "alcance"],
  ["Registro y uso de la plataforma", "registro"],
  ["Programación, corte y tiempos", "programacion"],
  ["Embalaje, contenido y restricciones", "embalaje"],
  ["Precios, facturación y pagos", "precios"],
  ["Contraentrega (pago en destino)", "contraentrega"],
  ["Entrega y comprobación", "entrega"],
  ["Devoluciones y retornos", "devoluciones"],
  ["Almacenamiento y abandono", "almacenamiento"],
  ["Responsabilidad y limitaciones", "responsabilidad"],
  ["Fuerza mayor", "fuerza-mayor"],
  ["Obligaciones del cliente", "obligaciones"],
  ["Datos personales", "datos"],
  ["Suspensión y terminación", "suspension"],
  ["Propiedad intelectual", "propiedad"],
  ["Cambios a los Términos", "cambios"],
  ["Ley aplicable y jurisdicción", "ley"],
  ["Contacto", "contacto"],
];

export default function TermsAndConditions() {
  return (
    <div className="bg-white text-slate-900">
      {/* Header */}
      <header className="border-b border-slate-100 bg-white/90 backdrop-blur">
        <div className={`${container} flex h-16 items-center justify-between`}>
          <Link to="/" className="flex items-center gap-2" aria-label="FAST MAX">
            <img src={`${process.env.PUBLIC_URL}/logo.png`} alt="FAST MAX" className="h-8 w-auto" />
            <span className="hidden sm:inline text-lg font-extrabold tracking-wide" style={{ color: brand.primary }}>
              FAST MAX
            </span>
          </Link>

          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-50 sm:text-sm sm:px-4 sm:py-2"
          >
            Volver al inicio
            <ChevronRight className="size-4" />
          </Link>
        </div>
      </header>

      {/* Body */}
      <main>
        <section className="bg-white">
          <div className={`${container} py-10 md:py-14`}>
            <motion.h1
              {...fadeUp}
              className="text-3xl md:text-4xl font-extrabold"
              style={{ color: brand.primary }}
            >
              Términos y Condiciones de Servicio
            </motion.h1>
            <motion.p {...fadeUp} transition={{ delay: 0.05 }} className="mt-2 text-slate-600">
              Última actualización: {new Date().toLocaleDateString("es-PE")}
            </motion.p>

            {/* Summary banner */}
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.1 }}
              className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm"
            >
              <p>
                Estos Términos y Condiciones (en adelante, los “Términos”) regulan el uso de los servicios de
                logística y transporte de paquetes que brinda <strong>FAST MAX</strong> en Lima Metropolitana y
                Callao. Al crear una cuenta, cargar pedidos (por ejemplo, vía Excel) o solicitar un envío, el
                cliente (el “Remitente”) acepta estos Términos.
              </p>
            </motion.div>

            {/* TOC */}
            <motion.div {...fadeUp} transition={{ delay: 0.15 }} className="mt-8 grid gap-2 sm:grid-cols-2">
            {toc.map(([label, id]) => (
                <a
                key={id}
                href={`#${id}`}
                className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold hover:bg-slate-50"
                onClick={scrollTo(id)}
                >
                {label}
                </a>
            ))}
            </motion.div>

            {/* Sections */}
            <Section id="alcance" title="1. Alcance del servicio">
              <ul className="list-outside space-y-2 pl-4 text-slate-700">
                <li>
                  <b>Ámbito:</b> FAST MAX presta servicios de <b>recogida, transporte y entrega de paquetes</b> dentro
                  de Lima Metropolitana y Callao, según cobertura publicada.
                </li>
                <li>
                  <b>Naturaleza del servicio:</b> FAST MAX actúa como <b>operador de transporte</b>. No es vendedor ni
                  intermediario del producto enviado ni se responsabiliza por la decisión del destinatario de aceptar o
                  rechazar el paquete o realizar pagos.
                </li>
                <li>
                  <b>Plataforma:</b> El cliente puede gestionar envíos mediante la plataforma FAST MAX (incluida
                  <b> carga masiva por Excel</b>), seguimiento en vivo, mensajería automática (API de WhatsApp) y
                  funcionalidades analíticas.
                </li>
              </ul>
            </Section>

            <Section id="registro" title="2. Registro y uso de la plataforma">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>El cliente es responsable de la veracidad y actualización de sus datos de registro.</li>
                <li>
                  El acceso a la cuenta es personal. El cliente debe resguardar sus credenciales y notificar de
                  inmediato accesos no autorizados.
                </li>
                <li>
                  El uso de la plataforma implica aceptar comunicaciones operativas (notificaciones de estado, mensajes
                  a destinatarios, conciliación, etc.).
                </li>
              </ul>
            </Section>

            <Section id="programacion" title="3. Programación, corte y tiempos">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  <b>Corte para mismo día:</b> pedidos registrados <b>hasta las 11:00 AM</b> se programan para entrega
                  el mismo día <b>antes de las 7:00 PM</b>, salvo causas de fuerza mayor, incidencias, o direcciones
                  incompletas/erróneas.
                </li>
                <li>
                  Los tiempos y ventanas de entrega son <b>estimados</b> y pueden variar por tráfico, clima, eventos,
                  reprogramaciones solicitadas o causas ajenas al control de FAST MAX.
                </li>
                <li>
                  Los intentos de entrega y reintentos (si aplica) se coordinan según política operativa y pueden
                  generar <b>costos adicionales</b>.
                </li>
              </ul>
            </Section>

            <Section id="embalaje" title="4. Embalaje, contenido y restricciones">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  El cliente es responsable del <b>embalaje adecuado</b> para proteger el contenido durante manipulación
                  y transporte, así como del <b>etiquetado correcto</b> (dirección, contacto y referencias).
                </li>
                <li>
                  No se aceptan <b>mercancías prohibidas</b> por ley o por política interna, incluyendo (de forma
                  enunciativa): armas, explosivos, sustancias peligrosas, drogas, artículos ilícitos, valores/cash (salvo
                  cobro de contraentrega según política), animales vivos, productos perecibles sin condiciones adecuadas,
                  objetos de alto valor sin seguro (joyas, obras de arte), documentación sensible sin acuerdo previo.
                </li>
                <li>
                  FAST MAX puede <b>rechazar</b> o <b>interrumpir</b> la prestación si detecta incumplimientos de
                  embalaje, documentación o restricciones.
                </li>
              </ul>
            </Section>

            <Section id="precios" title="5. Precios, facturación y pagos">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  Las tarifas se publican en la plataforma/sitio y pueden variar por <b>cobertura</b>, volumen, u
                  otros factores. Prevalece la tarifa vigente al momento de registrar el envío.
                </li>
                <li>
                  La facturación y medios de pago se informan en el panel o por los canales oficiales de FAST MAX.
                </li>
                <li>
                  Servicios adicionales (reintentos, devoluciones, almacenaje, zonas especiales) pueden tener{" "}
                  <b>cargos adicionales</b>.
                </li>
              </ul>
            </Section>

            <Section id="contraentrega" title="6. Contraentrega (pago en destino)">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  Cuando el cliente habilita la <b>contraentrega</b> (efectivo u otros medios permitidos), FAST MAX
                  actúa como <b>recaudador</b> por cuenta del cliente, conforme a la orden indicada.
                </li>
                <li>
                  <b>FAST MAX no es responsable</b> si el destinatario final <b>rechaza el paquete</b> o{" "}
                  <b>se niega a pagar</b>. En tal caso, el envío podrá ser reprogramado, devuelto o gestionado según
                  política de devoluciones (costos adicionales pueden aplicar).
                </li>
                <li>
                  La liquidación y depósito de montos recaudados se realizan conforme al <b>calendario operativo</b> y
                  el mecanismo de conciliación disponible en la plataforma.
                </li>
              </ul>
            </Section>

            <Section id="entrega" title="7. Entrega y comprobación">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  La entrega puede requerir <b>prueba de entrega</b> (firma, foto, PIN, nombre de receptor u otro
                  método), registrada en la plataforma.
                </li>
                <li>
                  Si la dirección es incorrecta, el destinatario no se encuentra disponible o solicita reprogramación,
                  el envío podrá <b>no entregarse</b> en la primera visita y generarse costos adicionales.
                </li>
              </ul>
            </Section>

            <Section id="devoluciones" title="8. Devoluciones y retornos">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  Los <b>retornos de paquetes</b> al cliente se realizan <b>una vez por semana</b>,{" "}
                  <b>sujetos a disponibilidad</b> y a la programación de rutas. Se informará el día o franja de retorno
                  disponible.
                </li>
                <li>
                  Rechazos por decisión del destinatario o <b>falta de pago</b> en contraentrega no son atribuibles a
                  FAST MAX. El retorno y/o reintento puede generar <b>cargos adicionales</b>.
                </li>
              </ul>
            </Section>

            <Section id="almacenamiento" title="9. Almacenamiento y abandono">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  Envíos no entregados/retornados pueden permanecer en <b>almacenamiento temporal</b> por un plazo
                  operativo razonable. Transcurrido dicho plazo sin instrucciones, el envío puede considerarse{" "}
                  <b>abandonado</b>, aplicándose cargos y disposiciones de la política interna.
                </li>
              </ul>
            </Section>

            <Section id="responsabilidad" title="10. Responsabilidad y limitaciones">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>
                  FAST MAX responde por <b>daños imputables</b> directamente a su operación de transporte, conforme a la
                  legislación aplicable y a sus políticas internas.
                </li>
                <li>
                  Salvo que el cliente contrate un <b>seguro adicional</b>, la responsabilidad por pérdida/daño se limita
                  a los <b>límites establecidos en la política de responsabilidad</b> de FAST MAX, disponible en el
                  panel o por solicitud.
                </li>
                <li>
                  FAST MAX <b>no responde</b> por: contenido prohibido/defectuoso/insuficientemente embalado; decisiones
                  del destinatario (rechazo o falta de pago); demoras por fuerza mayor; información incorrecta; ni por
                  consecuencias indirectas (lucro cesante, pérdida de chance, etc.).
                </li>
              </ul>
            </Section>

            <Section id="fuerza-mayor" title="11. Fuerza mayor">
              <p className="text-slate-700">
                Se entiende por fuerza mayor los eventos fuera del control razonable de FAST MAX (p. ej., desastres
                naturales, bloqueos, huelgas, fallas generalizadas de servicios, disposiciones de autoridad, etc.) que
                impidan o retrasen el cumplimiento. Durante estos eventos, las obligaciones podrán suspenderse o
                reprogramarse.
              </p>
            </Section>

            <Section id="obligaciones" title="12. Obligaciones del cliente">
              <ul className="space-y-2 pl-4 text-slate-700">
                <li>Proveer datos exactos (dirección, referencias, contacto) y etiquetas visibles.</li>
                <li>Cumplir con regulaciones y restricciones de contenido y embalaje.</li>
                <li>Informar a sus destinatarios sobre ventanas de entrega, contraentrega y políticas.</li>
                <li>Atender conciliaciones, incidencias y solicitudes de información en tiempo oportuno.</li>
              </ul>
            </Section>

            <Section id="datos" title="13. Datos personales">
              <p className="text-slate-700">
                FAST MAX trata datos personales de remitentes y destinatarios para finalidades operativas de
                logística, atención y conciliación, conforme a la normativa peruana de protección de datos personales
                (Ley N.° 29733 y su reglamento). El cliente declara haber informado a sus destinatarios y contar con las
                autorizaciones necesarias para comunicar sus datos a FAST MAX.
              </p>
            </Section>

            <Section id="suspension" title="14. Suspensión y terminación">
              <p className="text-slate-700">
                FAST MAX podrá suspender o finalizar el servicio ante incumplimientos, uso indebido, falta de pago,
                fraude o por razones de seguridad/operatividad, informando al cliente por los canales disponibles.
              </p>
            </Section>

            <Section id="propiedad" title="15. Propiedad intelectual">
              <p className="text-slate-700">
                La plataforma, marcas, software, interfaces, diseños y demás elementos son propiedad de FAST MAX o sus
                licenciantes. No se concede licencia ni uso distinto al necesario para operar el servicio.
              </p>
            </Section>

            <Section id="cambios" title="16. Cambios a los Términos">
              <p className="text-slate-700">
                FAST MAX podrá actualizar estos Términos para reflejar cambios legales u operativos. Las versiones
                vigentes estarán disponibles en el sitio/plataforma y podrán comunicarse por medios electrónicos.
              </p>
            </Section>

            <Section id="ley" title="17. Ley aplicable y jurisdicción">
              <p className="text-slate-700">
                Estos Términos se rigen por las leyes de la República del Perú. Para cualquier controversia, las partes
                se someten a la jurisdicción de los <b>juzgados y tribunales de Lima</b>, salvo disposición legal
                imperativa distinta.
              </p>
            </Section>

            <Section id="contacto" title="18. Contacto">
              <p className="text-slate-700">
                ¿Dudas o consultas? Escríbenos a <a href="mailto:contacto@fastmax.pe" className="underline">contacto@fastmax.pe</a>{" "}
                o contáctanos por WhatsApp.
              </p>
            </Section>

            {/* Back CTA */}
            <div className="mt-10 flex justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white shadow-sm"
                style={{ background: brand.primary }}
              >
                Volver al inicio
                <ChevronRight className="size-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

/* ---------- Small Section helper ---------- */
function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28">
      <motion.h2
        {...fadeUp}
        className="mt-10 text-xl font-extrabold"
        style={{ color: brand.primary }}
      >
        {title}
      </motion.h2>
      <motion.div {...fadeUp} transition={{ delay: 0.02 }} className="mt-3">
        {children}
      </motion.div>
    </section>
  );
}
